import { useCallback, useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import {
  Box,
  Typography,
  CircularProgress,
  Tooltip,
  Button,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import BannerImage from '../../components/banner/banner-image'
import ContentContainer from '../../areas/layout/content-container'
import LazyImage from '../../components/lazy-image'
import actorsApi from '../../client/actors-api'
import LargeCard from '../../components/cards/large-card'
import { ActorNotFound, LoadError } from '../Status/status-page'

type ActorDetail = {
  id: number
  name: string
  biography?: string
  profileUrl?: string
  birthday?: string
  placeOfBirth?: string
  department?: string
  credits: {
    id: number
    title: string
    type: 'movie' | 'show'
    posterUrl?: string
    year?: string
    rating: number
    character?: string
  }[]
}

const Wrapper = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(6),
}))

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(4),
  alignItems: 'flex-start',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
}))

const Profile = styled(Box)(({ theme }) => ({
  width: 220,
  height: 330,
  borderRadius: Number(theme.shape.borderRadius) * 2,
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  flexShrink: 0,
  [theme.breakpoints.down('md')]: {
    width: 180,
    height: 270,
  },
}))

const CreditsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: theme.spacing(3),
  marginTop: theme.spacing(4),

  [theme.breakpoints.down('md')]: {
    justifyItems: 'center',
  },
}))

const BioText = styled(Typography)(({ theme }) => ({
  display: '-webkit-box',
  WebkitLineClamp: 4,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  lineHeight: 1.6,
  maxWidth: 720,
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}))

const LoadingWrapper = styled(Wrapper)({
  display: 'flex',
  justifyContent: 'center',
})

const HeaderInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  alignItems: 'flex-start',
  [theme.breakpoints.down('md')]: {
    alignItems: 'center',
  },
}))

const BackLink = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(1),
  cursor: 'pointer',
  alignSelf: 'flex-start',
  [theme.breakpoints.down('md')]: {
    alignSelf: 'center',
  },
}))

const KnownForTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}))

const ActorDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [actor, setActor] = useState<ActorDetail | null>(null)
  const [displayCredits, setDisplayCredits] = useState<ActorDetail['credits']>(
    [],
  )
  const [creditsPage, setCreditsPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<number | null>(null)
  const [loadingMoreCredits, setLoadingMoreCredits] = useState(false)

  const load = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setError(null)
    setStatus(null)
    try {
      const res = await actorsApi.getById(Number(id))
      setActor(res.data)
      const sorted = res.data.credits
        .filter((c: any) => c.posterUrl)
        .sort((a: any, b: any) => (b.year || '').localeCompare(a.year || ''))
      setDisplayCredits(sorted.slice(0, 10))
      setCreditsPage(1)
    } catch (err) {
      setStatus(axios.isAxiosError(err) ? (err.response?.status ?? null) : null)
      setError('Failed to load actor')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    load()
  }, [load])

  const loadMoreCredits = () => {
    if (!actor || loadingMoreCredits) return
    setLoadingMoreCredits(true)
    setTimeout(() => {
      const sorted = actor.credits
        .filter((c) => c.posterUrl)
        .sort((a, b) => (b.year || '').localeCompare(a.year || ''))
      const nextSlice = sorted.slice(0, (creditsPage + 1) * 10)
      setDisplayCredits(nextSlice)
      setCreditsPage((p) => p + 1)
      setLoadingMoreCredits(false)
    }, 0)
  }

  if (loading) {
    return (
      <ContentContainer>
        <LoadingWrapper>
          <CircularProgress />
        </LoadingWrapper>
      </ContentContainer>
    )
  }

  if (error || !actor) {
    return status && status !== 404 ? (
      <LoadError onRetry={load} />
    ) : (
      <ActorNotFound />
    )
  }

  return (
    <>
      <BannerImage imageUrl={actor.profileUrl} blur={8} opacity={0.4}>
        <ContentContainer>
          <Wrapper>
            <Header>
              <Profile>
                <LazyImage
                  src={actor.profileUrl}
                  alt={actor.name}
                  fallback={
                    <Typography fontWeight={600}>{actor.name}</Typography>
                  }
                />
              </Profile>
              <HeaderInfo>
                <Typography variant='h4' fontWeight={700} color='text.primary'>
                  {actor.name}
                </Typography>
                {actor.department && (
                  <Typography variant='body2' color='text.secondary'>
                    {actor.department}
                  </Typography>
                )}
                {(actor.birthday || actor.placeOfBirth) && (
                  <Typography variant='body2' color='text.secondary'>
                    {actor.birthday && `Born: ${actor.birthday}`}
                    {actor.birthday && actor.placeOfBirth ? ' · ' : ''}
                    {actor.placeOfBirth}
                  </Typography>
                )}
                {actor.biography && (
                  <Tooltip title={actor.biography}>
                    <BioText variant='body1' color='text.secondary'>
                      {actor.biography}
                    </BioText>
                  </Tooltip>
                )}
                <BackLink
                  variant='button'
                  color='primary'
                  onClick={() => navigate(-1)}
                >
                  Back
                </BackLink>
              </HeaderInfo>
            </Header>
          </Wrapper>
        </ContentContainer>
      </BannerImage>

      <ContentContainer>
        <Wrapper>
          {displayCredits.length > 0 && (
            <>
              <KnownForTitle variant='h6' fontWeight={700} color='text.primary'>
                Known for
              </KnownForTitle>
              <CreditsGrid>
                {displayCredits.map((credit) => (
                  <LargeCard
                    key={`${credit.type}-${credit.id}`}
                    id={credit.id}
                    type={credit.type === 'movie' ? 'movie' : 'show'}
                    title={credit.title}
                    year={credit.year}
                    rating={credit.rating}
                    posterUrl={credit.posterUrl}
                  />
                ))}
              </CreditsGrid>
              {displayCredits.length < actor.credits.length && (
                <Box display='flex' justifyContent='center' mt={3}>
                  <Button
                    variant='outlined'
                    onClick={loadMoreCredits}
                    disabled={loadingMoreCredits}
                  >
                    {loadingMoreCredits ? 'Loading...' : 'Load more'}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Wrapper>
      </ContentContainer>
    </>
  )
}

export default ActorDetailPage
